import React, { useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from "@vercel/analytics/react"

// Import your sections/pages
import Home from './components/home/Home';
import AudienceSite from './components/audience/AudienceSite';
import { audienceFromLocation, audienceSites } from './components/audience/audienceSites';
import Benefits from './components/benefits/Benefits';
import ContactUs from './components/contact_us/ContactUs';
import Navbar from './components/navbar/Navbar';
import Pricing from './components/pricing/Pricing';
import Careers from './components/careers/Careers';
import Solutions from './components/solutions/Solutions';
import Footer from './components/footer/Footer';
import NotFound from './components/not_found/NotFound';
import { scrollOffsetForTarget } from './lib/scroll';

function ScrollToTop() {
  const { pathname, hash, state, key } = useLocation();
  const hashNavigationKey = hash ? key : null;
  const preserveScroll = Boolean(state?.preserveScroll);
  useEffect(() => {
    if (preserveScroll) return undefined;
    if (hash) {
      let cancelled = false;
      let frameId;
      let attempts = 0;
      const scrollToHash = () => {
        if (cancelled) return false;
        const target = document.getElementById(hash.slice(1));
        if (!target) {
          attempts += 1;
          if (attempts < 8) frameId = window.requestAnimationFrame(scrollToHash);
          return false;
        }
        const top =
          window.scrollY +
          target.getBoundingClientRect().top -
          scrollOffsetForTarget(target);
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reducedMotion ? "auto" : "smooth",
        });
        const heading = target.matches("h1, h2, h3") ? target : target.querySelector("h1, h2, h3");
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: true });
        }
        return true;
      };
      frameId = window.requestAnimationFrame(scrollToHash);
      document.fonts?.ready.then(() => {
        if (!cancelled) frameId = window.requestAnimationFrame(scrollToHash);
      });
      const fallbackId = window.setTimeout(() => {
        if (!cancelled && !document.getElementById(hash.slice(1))) {
          window.scrollTo(0, 0);
        }
      }, 150);
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(frameId);
        window.clearTimeout(fallbackId);
      };
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash, preserveScroll, hashNavigationKey]);
  return null;
}

function PageIdentity() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const previousHash = useRef(location.hash);
  useEffect(() => {
    const site = audienceSites[audienceFromLocation(location)];
    const inAudienceSite = site && (location.pathname === site.base || location.pathname.startsWith(`${site.base}/`));
    const segment = inAudienceSite ? location.pathname.slice(site.base.length).replace(/^\//, "") || "overview" : location.pathname.slice(1);
    const label = (inAudienceSite && (site.pageTitles?.[segment] || site.pages[segment])) || ({ contact: "Contact", solutions: "Solutions", engagements: "Engagement options", pricing: "Engagement options", evaluation: "How we test", benefits: "How we test", careers: "Careers" })[segment];
    document.title = label ? `${label} · Cornucopia Bio` : "Cornucopia Bio";
    let frame;
    if ((previousPath.current !== location.pathname || previousHash.current !== location.hash) && !location.hash && !location.state?.preserveScroll) {
      frame = window.requestAnimationFrame(() => {
        const heading = document.querySelector("main h1");
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          heading.focus({ preventScroll: true });
        }
      });
    }
    previousPath.current = location.pathname;
    previousHash.current = location.hash;
    return () => window.cancelAnimationFrame(frame);
  }, [location]);
  return null;
}

function LegacyInvestorRoute({ anchor }) {
  const { search, hash } = useLocation();
  return <Navigate replace to={{ pathname: "/investors", search, hash: hash || anchor }} />;
}

function AppLayout() {
  return (
    <>
      <ScrollToTop />
      <PageIdentity />
      <Navbar />
      <div className="App">
        <Routes>
          <Route path="/" element={<Home />} />
          {Object.entries(audienceSites).flatMap(([audience, site]) => [
            ...Object.keys(site.pages).map((page) => (
              <Route key={`${audience}/${page}`} path={`${site.base}${page === "overview" ? "" : `/${page}`}`} element={<AudienceSite audience={audience} page={page} />} />
            )),
            <Route key={`${audience}/contact`} path={`${site.base}/contact`} element={<ContactUs />} />,
          ])}
          <Route path="/scientists/solutions" element={<Solutions />} />
          <Route path="/investors/approach" element={<LegacyInvestorRoute anchor="#inside-the-lab" />} />
          <Route path="/investors/why-now" element={<LegacyInvestorRoute anchor="#why-now" />} />
          <Route path="/scientists/engagements" element={<Pricing />} />
          <Route path="/scientists/evaluation" element={<Benefits />} />
          <Route path="/benefits" element={<Benefits />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Footer />
      <Analytics />
    </>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppLayout />
    </Router>
  );
}

export default App;
